import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  LinkIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const StripeSettings = () => {
  const { token, demoMode } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    secretKey: '',
    publishableKey: '',
    webhookSecret: ''
  });
  const [testingConfig, setTestingConfig] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/stripe/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setConfig(data.config);
        if (!data.config.enabled || data.config.enabled === 'false') {
          setShowWizard(true);
        } else {
          loadAnalytics();
          loadInvoices();
        }
      }
    } catch (err) {
      setError('Failed to load Stripe configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch('/api/stripe/analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (err) {
      console.log('Analytics not available');
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await fetch('/api/stripe/invoices?limit=10', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.invoices);
      }
    } catch (err) {
      console.log('Invoices not available');
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/stripe/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: true,
          ...formData
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Stripe configuration saved successfully');
        setWizardStep(4);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save Stripe configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConfig(true);
    setTestResult(null);

    try {
      const response = await fetch('/api/stripe/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setTestResult({
          success: true,
          message: `Connected to Stripe account: ${data.account.businessName || data.account.id}`
        });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to connect to Stripe' });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Failed to connect to Stripe' });
    } finally {
      setTestingConfig(false);
    }
  };

  const handleDisableStripe = async () => {
    if (!window.confirm('Are you sure you want to disable the payment gateway? Existing payment links will stop working.')) {
      return;
    }

    try {
      const response = await fetch('/api/stripe/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: false })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Payment gateway disabled');
        await loadConfig();
        setShowWizard(true);
      }
    } catch (err) {
      setError('Failed to disable payment gateway');
    }
  };

  // Demo mode block
  if (demoMode?.enabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <CreditCardIcon className="h-8 w-8 text-gray-400" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Payment Gateway</h2>
            <p className="text-sm text-gray-500">Accept online payments via Stripe</p>
          </div>
        </div>

        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-6">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800">Not Available in Demo Mode</h3>
              <p className="mt-2 text-sm text-yellow-700">
                The payment gateway cannot be enabled while the application is running in demo mode.
                Payment processing requires a live environment to ensure the security of financial transactions.
              </p>
              <p className="mt-2 text-sm text-yellow-700">
                To enable Stripe payments, please deploy the application in a production environment with demo mode disabled.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Wizard view
  if (showWizard) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <CreditCardIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Payment Gateway Setup</h2>
            <p className="text-sm text-gray-500">Accept online payments from customers via Stripe</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                wizardStep >= step
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {wizardStep > step ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  step
                )}
              </div>
              {step < 4 && (
                <div className={`w-12 h-0.5 ${wizardStep > step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
              <p className="ml-3 text-sm text-green-700">{success}</p>
            </div>
          </div>
        )}

        {/* Step 1: Introduction */}
        {wizardStep === 1 && (
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Welcome to Payment Gateway Setup</h3>
            <p className="text-gray-600">
              This wizard will guide you through connecting your Stripe account to accept online payments from customers.
            </p>

            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5" />
                What you'll need:
              </h4>
              <ul className="text-sm text-blue-800 space-y-2 ml-7 list-disc">
                <li>A Stripe account (free to create at stripe.com)</li>
                <li>Your Stripe API keys (found in your Stripe Dashboard)</li>
                <li>Optional: A webhook secret for enhanced security</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-gray-900">How it works:</h4>
              <ol className="text-sm text-gray-700 space-y-2 ml-5 list-decimal">
                <li>You connect your Stripe account using your API keys</li>
                <li>From any sale with a remaining balance, you can send an invoice email</li>
                <li>The customer receives an email with a secure payment link</li>
                <li>They pay online via Stripe's secure checkout</li>
                <li>The payment is automatically recorded and a receipt is sent</li>
              </ol>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setWizardStep(2)}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Get Started
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Create Stripe Account */}
        {wizardStep === 2 && (
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Create or Access Your Stripe Account</h3>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-gray-900">If you don't have a Stripe account:</h4>
                <ol className="text-sm text-gray-700 space-y-2 ml-5 list-decimal">
                  <li>Visit <strong>stripe.com</strong> and click "Start now"</li>
                  <li>Create your account with your email address</li>
                  <li>Follow Stripe's onboarding to verify your business</li>
                  <li>Once verified, you'll have access to your API keys</li>
                </ol>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-gray-900">Finding your API keys:</h4>
                <ol className="text-sm text-gray-700 space-y-2 ml-5 list-decimal">
                  <li>Log in to your Stripe Dashboard</li>
                  <li>Click <strong>Developers</strong> in the top navigation</li>
                  <li>Click <strong>API keys</strong> in the sidebar</li>
                  <li>You'll see your <strong>Publishable key</strong> (starts with pk_) and <strong>Secret key</strong> (starts with sk_)</li>
                  <li>Click "Reveal" next to the Secret key to copy it</li>
                </ol>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800 flex items-start gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    <span><strong>Important:</strong> Use your <strong>live</strong> API keys for real payments, or <strong>test</strong> keys (starting with pk_test_ / sk_test_) to try it out first without processing real charges.</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setWizardStep(1)}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setWizardStep(3)}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                I Have My API Keys
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Enter API Keys */}
        {wizardStep === 3 && (
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Enter Your Stripe API Keys</h3>
            <p className="text-sm text-gray-600">
              Paste your API keys from the Stripe Dashboard below. These are stored securely and never shared.
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700">
                  Secret Key <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">Starts with sk_live_ or sk_test_</p>
                <input
                  type="password"
                  id="secretKey"
                  value={formData.secretKey}
                  onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                  placeholder="sk_live_..."
                />
              </div>

              <div>
                <label htmlFor="publishableKey" className="block text-sm font-medium text-gray-700">
                  Publishable Key
                </label>
                <p className="text-xs text-gray-500 mb-1">Starts with pk_live_ or pk_test_ (optional, for future use)</p>
                <input
                  type="text"
                  id="publishableKey"
                  value={formData.publishableKey}
                  onChange={(e) => setFormData({ ...formData, publishableKey: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                  placeholder="pk_live_..."
                />
              </div>

              <div className="border-t pt-4">
                <label htmlFor="webhookSecret" className="block text-sm font-medium text-gray-700">
                  Webhook Secret (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  For automatic payment confirmation. Find this in Stripe Dashboard → Developers → Webhooks
                </p>
                <input
                  type="password"
                  id="webhookSecret"
                  value={formData.webhookSecret}
                  onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                  placeholder="whsec_..."
                />
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800">
                    <strong>Webhook Setup (Recommended):</strong> In Stripe Dashboard → Developers → Webhooks, add an endpoint
                    pointing to <code className="bg-blue-100 px-1 rounded">[your-site-url]/api/stripe/webhook</code> and
                    listen for <code className="bg-blue-100 px-1 rounded">checkout.session.completed</code> events.
                    This ensures payments are recorded automatically even if the customer closes their browser.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setWizardStep(2)}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (!formData.secretKey) {
                    setError('Secret key is required');
                    return;
                  }
                  setError('');
                  handleSaveConfig();
                }}
                disabled={!formData.secretKey}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                Save & Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Test Connection */}
        {wizardStep === 4 && (
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Test Your Connection</h3>
            <p className="text-sm text-gray-600">
              Let's verify that your Stripe account is connected correctly.
            </p>

            <div className="space-y-4">
              <button
                onClick={handleTestConnection}
                disabled={testingConfig}
                className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                {testingConfig ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Testing Connection...
                  </span>
                ) : (
                  'Test Stripe Connection'
                )}
              </button>

              {testResult && (
                <div className={`rounded-md p-4 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-start gap-3">
                    {testResult.success ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                    )}
                    <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-green-900 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5" />
                You're all set!
              </h4>
              <p className="mt-1 text-sm text-green-800">
                Your payment gateway is now configured. You can send invoice emails with payment links from any sale's detail view.
              </p>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setWizardStep(3)}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => {
                  setShowWizard(false);
                  loadConfig();
                }}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Configured view - show status, analytics, and recent invoices
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCardIcon className="h-8 w-8 text-green-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Payment Gateway</h2>
            <p className="text-sm text-gray-500">Stripe is connected and accepting payments</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setWizardStep(3);
              setShowWizard(true);
            }}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
            Update Keys
          </button>
          <button
            onClick={handleDisableStripe}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50"
          >
            Disable
          </button>
        </div>
      </div>

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-3 w-3 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-sm font-medium text-gray-700">Connected</span>
            {config?.secretKeyPreview && (
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                {config.secretKeyPreview}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <ShieldCheckIcon className="h-4 w-4" />
              {config?.hasWebhookSecret ? 'Webhook configured' : 'No webhook'}
            </span>
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Collected</p>
            <p className="text-2xl font-semibold text-gray-900">
              ${analytics.totalPaidAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-4">
            <p className="text-sm text-gray-500">Invoices Sent</p>
            <p className="text-2xl font-semibold text-gray-900">{analytics.totalSentCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-4">
            <p className="text-sm text-gray-500">Payments Received</p>
            <p className="text-2xl font-semibold text-gray-900">{analytics.totalPaidCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200 p-4">
            <p className="text-sm text-gray-500">Conversion Rate</p>
            <p className="text-2xl font-semibold text-gray-900">{analytics.conversionRate}%</p>
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      {invoices.length > 0 && (
        <div className="bg-white rounded-lg shadow ring-1 ring-gray-200">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-700">Recent Payment Invoices</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Sale #{invoice.saleId} - {invoice.Contact ? `${invoice.Contact.firstName} ${invoice.Contact.lastName}` : 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Sent {new Date(invoice.createdAt).toLocaleDateString()}
                    {invoice.emailOpenedAt && (
                      <span className="ml-2 text-blue-600">Opened {new Date(invoice.emailOpenedAt).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">
                    ${parseFloat(invoice.amount).toFixed(2)}
                  </span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                    invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {invoice.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How to Use */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-sm font-medium text-blue-900 flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5" />
          How to send a payment invoice
        </h4>
        <ol className="mt-2 text-sm text-blue-800 space-y-1 ml-7 list-decimal">
          <li>Navigate to <strong>Sales</strong> and click on any sale with a remaining balance</li>
          <li>Click the <strong>"Send Payment Invoice"</strong> button in the Payments section</li>
          <li>An invoice email with a payment link will be sent to the contact</li>
          <li>The customer pays securely through Stripe</li>
          <li>Payment is recorded automatically and a receipt is sent</li>
        </ol>
      </div>
    </div>
  );
};

export default StripeSettings;
